import { Loader2, AlertTriangle, ShieldAlert, ShieldCheck, ShieldQuestion, CircleHelp } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * Analytics / Object Version Status Codes — ADVISORY, never a safety verdict.
 *
 * 0  = Not scanned (grey)
 * 1  = Scanning (animated dots)
 * 2  = Confirmed malicious content (RED)      — a confirmed detection was reported
 * 3  = Indicators detected (AMBER)            — heuristic / dual-use signals, none confirmed
 * 4  = No confirmed detections (GREEN)        — nothing confirmed in what we scanned (NOT "clean/safe")
 * 5  = Indicators detected (AMBER)            — findings present, none confirmed (same advisory as 3)
 * 10 = Scan did not complete (warning)
 *
 * HARD PRODUCT RULE: this is decision-support, never a verdict. GREEN means "no threats
 * detected in what we scanned" — never "clean", "safe", or "pass". A scanner cannot prove the
 * absence of a threat. When coverage is incomplete (a volume was skipped/encrypted), GREEN is
 * downgraded to a NEUTRAL "incomplete coverage" advisory (see `coverageIncomplete`).
 *
 * NOTE: `color` (dot bg) and `hex` (chart nodes) are kept close to their long-standing values so
 * existing dots/charts are undisturbed — the only status re-coloured is 5 (was green, now amber,
 * because indicators must never read as green/safe). Colourblind-safe icon+label rendering is
 * available via the `showLabel` chip (used for the at-a-glance object RAG).
 */

export type AnalyticsStatus = 0 | 1 | 2 | 3 | 4 | 5 | 10

export interface StatusConfig {
    label: string
    advisory: string     // short advisory line (tooltip / chip sub-text) — never "safe/clean"
    color: string        // Tailwind bg class for dots
    textColor: string    // Tailwind text class
    ringColor: string    // Tailwind ring class
    hex: string          // Hex colour for visualisation
    solid: string        // The DOT colour as hex (same shade as `color`) — for marks a class cannot colour, e.g. the hierarchy's network icon
    pulse: string        // Pulse animation class (empty = no pulse)
    type: 'dot' | 'spinner' | 'icon'  // How to render
    Icon: LucideIcon     // Distinct icon per state — colour is NEVER the only signal
}

const STATUS_MAP: Record<number, StatusConfig> = {
    0: {
        label: 'Not scanned',
        advisory: 'This recovery point has not been scanned.',
        color: 'bg-gray-300 dark:bg-gray-600',
        solid: '#D1D5DB',
        textColor: 'text-gray-500 dark:text-gray-400',
        ringColor: 'ring-gray-300',
        hex: '#CCCCCC',
        pulse: '',
        type: 'dot',
        Icon: CircleHelp,
    },
    1: {
        label: 'Scanning',
        advisory: 'Analytics scan in progress.',
        color: 'bg-blue-400',
        solid: '#60A5FA',
        textColor: 'text-blue-500',
        ringColor: 'ring-blue-300',
        hex: '#60A5FA',
        pulse: 'animate-pulse',
        type: 'spinner',
        Icon: Loader2,
    },
    2: {
        label: 'Confirmed malicious',
        advisory: 'Confirmed malicious content — remediate before restoring.',
        color: 'bg-red-500',
        solid: '#EF4444',
        textColor: 'text-red-600 dark:text-red-400',
        ringColor: 'ring-red-300',
        hex: '#FFB3B3',
        pulse: 'animate-analytics-pulse-red',
        type: 'dot',
        Icon: ShieldAlert,
    },
    3: {
        label: 'Indicators detected',
        advisory: 'Indicators detected — review before restoring.',
        color: 'bg-amber-500',
        solid: '#F59E0B',
        textColor: 'text-amber-600 dark:text-amber-400',
        ringColor: 'ring-amber-300',
        hex: '#FFD699',
        pulse: 'animate-analytics-pulse-amber',
        type: 'dot',
        Icon: AlertTriangle,
    },
    4: {
        // GREEN = no CONFIRMED detection in what we scanned. The wording is a legal constraint
        // (scanner df8a055): the report may be relied on in litigation, so never "clean", "safe"
        // or "no threats" — posture and telemetry findings can sit under a 4, and "no threats"
        // would be a claim about them. Coverage-aware: when a volume was skipped, callers pass
        // coverageIncomplete to downgrade to neutral.
        label: 'No confirmed detections',
        advisory: 'No confirmed detections in what we scanned. Not a clean bill of health.',
        color: 'bg-green-500',
        solid: '#22C55E',
        textColor: 'text-green-600 dark:text-green-400',
        ringColor: 'ring-green-300',
        hex: '#B4E197',
        pulse: '',
        type: 'dot',
        Icon: ShieldCheck,
    },
    5: {
        // Findings present, none confirmed — the SAME advisory as code 3, so it renders identically
        // (AMBER dot). Indicators must never read as green/safe, and this must match the scan-report
        // "Indicators only" advisory shown in the modal. The hierarchy rollup is unaffected: the
        // ObjectStatus view normalises its rolled-up `analytics` column to 0/2/3/4 (never 5), so
        // recolouring 5 only touches where a per-version / object-only code 5 is displayed directly.
        label: 'Indicators detected',
        advisory: 'Indicators detected — review before restoring.',
        color: 'bg-amber-500',
        solid: '#F59E0B',
        textColor: 'text-amber-600 dark:text-amber-400',
        ringColor: 'ring-amber-300',
        hex: '#FFD699',
        pulse: 'animate-analytics-pulse-amber',
        type: 'dot',
        Icon: AlertTriangle,
    },
    10: {
        label: 'Scan did not complete',
        advisory: 'The scan did not complete — status is undetermined.',
        color: 'bg-yellow-500',
        solid: '#EAB308',
        textColor: 'text-yellow-600',
        ringColor: 'ring-yellow-300',
        hex: '#F59E0B',
        pulse: '',
        type: 'icon',
        Icon: AlertTriangle,
    },
}

// Neutral state shown when the scan found no threats (code 4) BUT coverage was incomplete
// (a volume was skipped/encrypted/timed out). Green would over-state confidence, so we show a
// grey, honestly-hedged "incomplete coverage" advisory instead.
const INCOMPLETE_COVERAGE_CONFIG: StatusConfig = {
    label: 'Incomplete coverage',
    advisory: 'Informational — not everything could be scanned. Review coverage before relying on this.',
    color: 'bg-slate-400 dark:bg-slate-500',
    solid: '#94A3B8',
    textColor: 'text-slate-600 dark:text-slate-300',
    ringColor: 'ring-slate-300',
    hex: '#94A3B8',
    pulse: '',
    type: 'dot',
    Icon: ShieldQuestion,
}

const DEFAULT_STATUS = STATUS_MAP[0]

/**
 * Get the full status config for an analytics code. When `coverageIncomplete` is true and the
 * code is a "no threats detected" green (4), the neutral incomplete-coverage config is returned
 * so a confident green is never shown over a partial scan.
 */
export function getAnalyticsConfig(
    status: number | null | undefined,
    coverageIncomplete = false,
): StatusConfig {
    if (status == null) return DEFAULT_STATUS
    if (status === 4 && coverageIncomplete) return INCOMPLETE_COVERAGE_CONFIG
    return STATUS_MAP[status] || DEFAULT_STATUS
}

/**
 * Get the colour class for a status dot
 */
export function getAnalyticsColor(status: number | null | undefined, coverageIncomplete = false): string {
    return getAnalyticsConfig(status, coverageIncomplete).color
}

/**
 * Get the hex colour for visualisation nodes
 */
export function getAnalyticsHex(status: number | null | undefined, coverageIncomplete = false): string {
    return getAnalyticsConfig(status, coverageIncomplete).hex
}

/**
 * Get the status label (advisory — never "clean/safe/pass")
 */
export function getAnalyticsLabel(status: number | null | undefined, coverageIncomplete = false): string {
    return getAnalyticsConfig(status, coverageIncomplete).label
}

/**
 * Render a status indicator.
 *
 * Compact form (default): the long-standing coloured dot, now carrying an advisory tooltip +
 * aria-label. Set `showLabel` to render the at-a-glance RAG CHIP — a distinct icon + text label
 * + colour, so colour is never the only signal (colourblind-safe).
 *
 * @param status - Analytics status code (0, 1, 2, 3, 4, 5, 10)
 * @param size - Size class for the dot (default: 'h-3 w-3')
 * @param showRing - Whether to show the outer ring (default: false)
 * @param showLabel - Render as an icon + text chip (the at-a-glance RAG chip) instead of a bare dot
 * @param coverageIncomplete - Downgrade a "no threats detected" green to a neutral incomplete-coverage state
 */
export function AnalyticsStatusIndicator({
    status,
    size = 'h-3 w-3',
    showRing = false,
    showLabel = false,
    showIcon = false,
    coverageIncomplete = false,
}: {
    status: number | null | undefined
    size?: string
    showRing?: boolean
    showLabel?: boolean
    showIcon?: boolean
    coverageIncomplete?: boolean
}) {
    const config = getAnalyticsConfig(status, coverageIncomplete)
    const Icon = config.Icon

    // At-a-glance RAG chip: icon + colour + text label (colourblind-safe, advisory tooltip).
    if (showLabel) {
        if (config.type === 'spinner') {
            return (
                <span className="inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" title={config.advisory}>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {config.label}
                </span>
            )
        }
        return (
            <span
                className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium ${config.textColor} bg-black/[0.04] dark:bg-white/[0.06]`}
                title={config.advisory}
            >
                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{config.label}</span>
            </span>
        )
    }

    // In progress — animated dots
    if (config.type === 'spinner') {
        return (
            <span className={`inline-flex items-center gap-[2px] ${size}`} title={config.advisory} aria-label={config.label}>
                <span className="rounded-full bg-blue-500 animate-bounce" style={{ width: '4px', height: '4px', animationDelay: '0ms' }} />
                <span className="rounded-full bg-blue-500 animate-bounce" style={{ width: '4px', height: '4px', animationDelay: '150ms' }} />
                <span className="rounded-full bg-blue-500 animate-bounce" style={{ width: '4px', height: '4px', animationDelay: '300ms' }} />
            </span>
        )
    }

    // Failed — warning triangle
    if (config.type === 'icon') {
        return <AlertTriangle className={`${size} text-yellow-600`} title={config.advisory} aria-label={config.label} />
    }

    // Coloured RAG circle PLUS the advisory icon to its right (no text) — the circle keeps the
    // red/amber/green the hierarchy uses; the icon adds the colourblind-safe advisory shape.
    if (showIcon) {
        return (
            <span className="relative inline-flex items-center gap-1.5" title={config.advisory} aria-label={config.label}>
                <span className="relative inline-flex">
                    {showRing && config.pulse && (
                        <span className={`absolute inset-0 rounded-full ${config.color} opacity-30 ${config.pulse}`} />
                    )}
                    <span className={`${size} rounded-full ${config.color} ${config.pulse}`} />
                </span>
                <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${config.textColor}`} />
            </span>
        )
    }

    // Compact dot (Not scanned, Confirmed, Indicators, No threats, Incomplete coverage).
    return (
        <span className="relative inline-flex items-center" title={config.advisory} aria-label={config.label}>
            <span className="relative inline-flex">
                {showRing && config.pulse && (
                    <span className={`absolute inset-0 rounded-full ${config.color} opacity-30 ${config.pulse}`} />
                )}
                <span className={`${size} rounded-full ${config.color} ${config.pulse}`} />
            </span>
        </span>
    )
}
